import React from 'react'
import { Box, Paper, Typography, Stack, TextField, Button, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material'
import { Save as SaveIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'

type NewsPayload = {
  title: string
  content: string
  author?: string
  category?: string
  imageUrl?: string
  summary?: string
  published?: boolean
}

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

const NewPostPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toastPromise } = useToast()
  const toastMessages = useToastMessages()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<NewsPayload>({
    defaultValues: {
      title: '',
      content: '',
      author: '',
      category: '',
      imageUrl: '',
      summary: '',
      published: false
    }
  })

  const [isSubmitting, setSubmitting] = React.useState(false)
  const pendingSubmission = React.useRef(false)
  const [errorDlg, setErrorDlg] = React.useState<{ open: boolean, message: string }>({ open: false, message: '' })

  const onSubmit = async (data: NewsPayload) => {
    if (pendingSubmission.current) return
    pendingSubmission.current = true
    setSubmitting(true)
    const promise = fetchWithAuth(buildApiUrl('/news'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(async (res) => {
      if (!res.ok) {
        let msg = ''
        try {
          const jsonData = await res.clone().json()
          msg = jsonData?.detail || jsonData?.message || ''
        } catch { /* ignore */ }
        if (!msg) { try { msg = await res.text() } catch { /* ignore */ } }
        throw new Error(msg || `Fehler (${res.status})`)
      }
      return res
    })

    return toastPromise(promise, {
      loading: toastMessages.saving(),
      success: () => {
        reset()
        navigate('/beitraege')
        return t('admin.newPost') + ' ✓'
      },
      error: (e: any) => {
        const msg = e?.message || t('dialog.error')
        setErrorDlg({ open: true, message: msg })
        return msg
      },
    }).catch(() => {
      // The dialog and toast show the error; preserve the form for retry.
    }).finally(() => {
      pendingSubmission.current = false
      setSubmitting(false)
    })
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('admin.newPost')}</Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Titel"
              {...register('title', {
                required: 'Titel ist erforderlich',
                minLength: { value: 3, message: 'Mindestens 3 Zeichen erforderlich' }
              })}
              error={!!errors.title}
              helperText={errors.title?.message}
              required
              fullWidth
              disabled={isSubmitting}
            />

            <TextField
              label="Zusammenfassung"
              {...register('summary')}
              fullWidth
              disabled={isSubmitting}
              multiline
              rows={2}
            />

            <TextField
              label="Kategorie"
              {...register('category')}
              fullWidth
              disabled={isSubmitting}
            />

            <TextField
              label="Autor"
              {...register('author')}
              fullWidth
              disabled={isSubmitting}
            />

            <TextField
              label="Bild URL"
              {...register('imageUrl', {
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Bitte eine gültige URL eingeben'
                }
              })}
              error={!!errors.imageUrl}
              helperText={errors.imageUrl?.message}
              fullWidth
              disabled={isSubmitting}
            />

            <TextField
              label="Inhalt"
              {...register('content', {
                required: 'Inhalt ist erforderlich',
                minLength: { value: 10, message: 'Mindestens 10 Zeichen erforderlich' }
              })}
              error={!!errors.content}
              helperText={errors.content?.message}
              multiline
              minRows={8}
              fullWidth
              required
              disabled={isSubmitting}
            />

            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label="Sofort veröffentlichen"
                />
              )}
            />

            <Box>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {isSubmitting ? 'Erstellen...' : t('btn.create')}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
      <Dialog open={errorDlg.open} onClose={() => setErrorDlg({ open: false, message: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dialog.createError')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{errorDlg.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDlg({ open: false, message: '' })}>{t('btn.ok')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default NewPostPage